import { createEmitter } from '@codesuma/baseline'
import type { RealtimeChannel } from '@supabase/supabase-js'
import supabase, { supabase as sbClient } from './supabase'
import { emitter as appEmitter } from './emitter'
import listsService from './lists'
import { InvitePrompt } from '../components/invite-prompt'

// App-wide incoming-share alerts. This service is the single owner of the
// invite overlay so it can appear on ANY page, not just home: pages call
// check() at natural moments (app open, back to home), and a Realtime
// subscription on the user's own list_members rows pops it live the moment
// someone shares a list with them. Emits 'accepted' when a list is joined.

const emitter = createEmitter()

let prompt: ReturnType<typeof InvitePrompt> | null = null
let channel: RealtimeChannel | null = null

const getPrompt = () => {
    if (!prompt) {
        prompt = InvitePrompt()
        prompt.on('accepted', () => emitter.emit('accepted'))
    }
    return prompt
}

// Fetch pending invites and surface the overlay. No-op when signed out,
// already showing, or there's nothing pending.
const check = async (): Promise<void> => {
    if (!supabase.auth.isAuthenticated()) return
    const p = getPrompt()
    if (p.el.parentNode) return
    try {
        const invites = await listsService.getPendingInvites()
        if (invites.length) {
            document.body.appendChild(p.el)
            p.show(invites)
        }
    } catch (err) {
        console.error('Failed to check invites:', err)
    }
}

// Live path: a row inserted into list_members for me = someone shared a list.
// RLS lets a user see their own membership rows, so the filtered subscription
// only ever delivers this user's invites.
const subscribe = () => {
    const token = supabase.auth.getToken()
    const uid = supabase.auth.getUser()?.id
    if (!token || !uid || channel) return
    sbClient.realtime.setAuth(token)
    channel = sbClient
        .channel(`invites-${uid}`)
        .on('postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'list_members', filter: `user_id=eq.${uid}` },
            (payload: any) => {
                if (payload.new?.status === 'pending') check()
            })
        .subscribe()
}

// Boot: check once and start listening. The session can also appear later
// (anon user minted on first write, or sign-in) — subscribe then instead.
const init = async (): Promise<void> => {
    appEmitter.on('auth:change', ({ isAuthenticated }: { isAuthenticated: boolean }) => {
        if (isAuthenticated) subscribe()
    })
    await supabase.auth.waitForInitialization()
    if (supabase.auth.isAuthenticated()) {
        subscribe()
        check()
    }
}

export default {
    init,
    check,
    on: emitter.on.bind(emitter),
}
